import React, { useState } from 'react';
import styled from 'styled-components/macro';
import { Dialog, DialogTitle, Button } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClose, faLink, faCheck } from '@fortawesome/pro-solid-svg-icons';

interface ShareDialogProps {
  isOpen: boolean;
  onClose: () => void;
  name?: string;
  jobName?: string;
  shareUrl: string;
}

export function ShareDialog({ isOpen, onClose, name, jobName, shareUrl }: ShareDialogProps) {
  const [linkCopied, setLinkCopied] = useState(false);

  const shareText = jobName ? "Check out my song on Controlla Voice: " + jobName : "Check out my song on Controlla Voice!";
  
  const handleLinkAreaClick = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy link:', err);
    }
  };

  const handleSystemShare = async () => {

    // Use the native share API if availabletry {
    console.log(shareUrl)
    await navigator.share({
        title: shareText,
        url: shareUrl,
    });
  
  };

  const handleClose = () => {
    setLinkCopied(false);
    onClose();
  };

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle style={{ textAlign: 'left', fontSize: '24px'}}>
        Share as {name || 'Untitled'}
        <Button
          sx={{ float: 'right' }}
          onClick={handleClose}
        >
          <FontAwesomeIcon icon={faClose} size='2x' />
        </Button>
      </DialogTitle>
      
      <SocialShareContainer>        
        <ShareLinkContainer>
          <SharedLinkBox onClick={handleLinkAreaClick}>
            <LinkText>
              <FontAwesomeIcon icon={faLink} size='2xs' color='rgba(245, 225, 255, 0.74))' />
              <LinkTextTypography>
                {linkCopied ? "Link copied!" : shareUrl}
              </LinkTextTypography>
            </LinkText>
          </SharedLinkBox>
          <CopyLinkButton
            sx={{ padding: '25px' }}
            onClick={handleLinkAreaClick}
          >
            Copy
          </CopyLinkButton>
        </ShareLinkContainer>
        
        <MobileShareButton className="primary-button" onClick={handleSystemShare}>
          Share
        </MobileShareButton>
      </SocialShareContainer>
    </Dialog>
  );
}

const CopyLinkButton = styled(Button)`
  display: flex;
  height: 50px;
  padding: 6px 16px;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  border-radius: 15px;
  background: var(--Button-Active, linear-gradient(180deg, #7D0EB5 0%, #4D0671 100%));
  
  /* elevation/2 */
  box-shadow: 0px 1px 5px 0px rgba(0, 0, 0, 0.12), 0px 2px 2px 0px rgba(0, 0, 0, 0.14), 0px 3px 1px -2px rgba(0, 0, 0, 0.20);

  @media only screen and (max-width: 430px) {
    display: none !important;
  }
`;

const LinkText = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  min-width:0;
  max-width:350px;
  @media only screen and (max-width: 600px) {
    max-width:150px;
  }
`;

const LinkTextTypography = styled.div`
  color: var(--text-primary, #FFF);
  font-feature-settings: 'liga' off, 'clig' off;
  /* typography/body1 */
  font-family: Raleway;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 150%; /* 24px */
  letter-spacing: 0.15px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const SharedLinkBox = styled.div`
  display: flex;
  height: 50px;
  padding: 25px 23px;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: 10px;
  flex: 1 0 0;
  border-radius: 20px;
  border: 2px solid rgba(245, 225, 255, 0.74);
  background: #07000B;
  cursor: pointer;
  transition: border-color 0.2s ease, background 0.2s ease;
  user-select: none;
  
  &:hover {
    border-color: #FF74E9;
    background: #0a0015;
  }
`;

const SocialShareContainer = styled.div`
  padding: 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
`;

const SocialButtonsRow = styled.div`
  display: flex;
  gap: 20px;
  justify-content: center;
  flex-wrap: wrap;
`;

const SocialButton = styled.button<{ color: string }>`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  border: none;
  background: ${props => props.color};
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    transform: scale(1.1);
    box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.3);
  }
  
  &:active {
    transform: scale(0.95);
  }
`;

const OrDivider = styled.div`
  color: rgba(245, 225, 255, 0.74);
  font-family: Raleway;
  font-size: 16px;
  font-weight: 400;
  text-align: center;
  margin: 10px 0;
`;

const ShareLinkContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 21px;
  align-self: stretch;
  width: 100%;
`;

const MobileShareButton = styled(Button)`
  && {
    width: 100%;
    height: 50px;
    margin-top: 20px;
    border-radius: 15px;
    background: var(--Button-Active, linear-gradient(180deg, #7D0EB5 0%, #4D0671 100%));
    color: #FFF;
    font-family: Raleway;
    font-size: 16px;
    font-weight: 500;
    text-transform: none;
    box-shadow: 0px 1px 5px 0px rgba(0, 0, 0, 0.12), 0px 2px 2px 0px rgba(0, 0, 0, 0.14), 0px 3px 1px -2px rgba(0, 0, 0, 0.20);
    
    &:hover {
      background: var(--Button-Active, linear-gradient(180deg, #8B15C7 0%, #5A0883 100%));
    }
  }
`;